import React, { useState, useEffect } from 'react';
import moment from 'moment';
import getJwtPayload from '../../utils/methods/getJwtPayload';
import { withTranslation } from '../Translator';
import StatusBar from '../StatusBar';
import getCheckoutLink from '../../utils/methods/getCheckoutLink';

const PAYING_LICENSE_TYPE = 'paying';
// thresholds in days
const THRESHOLDS = {
  ANALYTICS_FIRST: 60,
  ANALYTICS_SECOND: 30,
  ANALYTICS_PERSISTANCE: 5,
  AUDIT_LOG_PERSISTANCE: 5,
  AUDIT_LOG: 35,
};
const LOCAL_STORAGE_NAMES = {
  ANALYTICS: 'ppas-data-retention-period-reached-banner-closed-timestamp',
  AUDIT_LOG: 'ppas-audit-log-data-retention-period-reached-banner-closed-timestamp',
};
const API_URLS = {
  ANALYTICS: '/api/data-retention/v1',
  AUDIT_LOG: '/api/audit-log/v1/retention',
};

function getRequestConfig({ jwt }) {
  const headers = {
    'Content-Type': 'application/vnd.api+json',
    Authorization: `Bearer ${jwt}`,
  };

  return {
    method: 'GET',
    headers,
  };
}

function useAnalyticsDataRetentionInfo({ isOwner, jwt }) {
  const [data, setData] = useState({
    retention_period: null,
    start_date: null,
  });

  useEffect(() => {
    if (isOwner) {
      fetch(API_URLS.ANALYTICS, getRequestConfig({ jwt }))
        .then(resp => resp.json())
        .then((resp) => {
          setData(resp);
        })
        .catch(() => {});
    }
  }, []);

  return data;
}

function useAuditLogDataRetentionInfo({ isOwner, jwt }) {
  const [data, setData] = useState({
    current_period: null,
    oldest_event: null,
  });

  useEffect(() => {
    if (isOwner) {
      fetch(API_URLS.AUDIT_LOG, getRequestConfig({ jwt }))
        .then(resp => resp.json())
        .then((resp) => {
          setData(resp);
        })
        .catch(() => {});
    }
  }, []);

  return data;
}

function getClosedBannerTimestamp(bannerStorageName) {
  return localStorage.getItem(bannerStorageName);
}

function setClosedBannerTimestamp(bannerStorageName, date = moment().toISOString()) {
  localStorage.setItem(bannerStorageName, date);
}

function removeClosedBannerTimestamp(bannerStorageName) {
  localStorage.removeItem(bannerStorageName);
}

const coreContent = (organizationName, t) => ({
  text: t('DataRetentionBanner.core-and-paying.text'),
  linkText: t('DataRetentionBanner.core.link-button.text'),
  onLinkClick: () => window.open(
    getCheckoutLink({ organizationName }),
    '_blank',
  ),
});
const payingContent = t => ({
  text: t('DataRetentionBanner.core-and-paying.text'),
  linkText: t('DataRetentionBanner.paying.link-button.text'),
  onLinkClick: () => window.open('mailto:support@piwik.pro?subject=Extension of data retention period', '_self'),
});

function shouldShowAnalyticsBanner({ retentionPeriod, startDate, currentDate }) {
  const reachDate = moment(startDate).add(retentionPeriod, 'months');

  const isInsideTimeWindow = (which = 'first', timestamp = moment(currentDate)) => {
    const daysToReach = moment(reachDate).startOf('day').diff(moment(timestamp.startOf('day')), 'days');

    if (which === 'first') {
      return (daysToReach <= THRESHOLDS.ANALYTICS_FIRST && daysToReach >= THRESHOLDS.ANALYTICS_FIRST - THRESHOLDS.ANALYTICS_PERSISTANCE);
    }

    return (daysToReach <= THRESHOLDS.ANALYTICS_SECOND && daysToReach >= THRESHOLDS.ANALYTICS_SECOND - THRESHOLDS.ANALYTICS_PERSISTANCE);
  };

  const isInsideAnyTimeWindow = isInsideTimeWindow('first') || isInsideTimeWindow('second');
  const closedBannerTimestamp = getClosedBannerTimestamp(LOCAL_STORAGE_NAMES.ANALYTICS) ? moment(getClosedBannerTimestamp(LOCAL_STORAGE_NAMES.ANALYTICS)) : null;

  if (!closedBannerTimestamp && isInsideAnyTimeWindow) {
    return true;
  }

  if (closedBannerTimestamp) {
    if ((
      isInsideTimeWindow('first') && isInsideTimeWindow('first', closedBannerTimestamp))
      || (
        isInsideTimeWindow('second') && isInsideTimeWindow('second', closedBannerTimestamp)
      )) {
      return false;
    }

    if (isInsideAnyTimeWindow) {
      return true;
    }

    removeClosedBannerTimestamp(LOCAL_STORAGE_NAMES.ANALYTICS);

    return false;
  }

  return false;
}

function shouldShowAuditLogBanner({ retentionPeriod, startDate, currentDate }) {
  const reachDate = moment(startDate).add(retentionPeriod, 'months');

  const isInsideTimeWindow = () => {
    const today = moment(currentDate);
    const daysToReach = moment(reachDate).startOf('day').diff(moment(today.startOf('day')), 'days');

    return (daysToReach <= THRESHOLDS.AUDIT_LOG && daysToReach >= THRESHOLDS.AUDIT_LOG - THRESHOLDS.AUDIT_LOG_PERSISTANCE);
  };

  const closedBannerTimestamp = getClosedBannerTimestamp(LOCAL_STORAGE_NAMES.AUDIT_LOG) ? moment(getClosedBannerTimestamp(LOCAL_STORAGE_NAMES.AUDIT_LOG)) : null;

  if (!closedBannerTimestamp && isInsideTimeWindow()) {
    return true;
  }

  if (closedBannerTimestamp && isInsideTimeWindow()) {
    return false;
  }

  removeClosedBannerTimestamp(LOCAL_STORAGE_NAMES.AUDIT_LOG);

  return false;
}

function prepareBannerConfig({ licensePlanType, organizationName, t }) {
  if (licensePlanType === PAYING_LICENSE_TYPE) {
    return payingContent(t);
  }

  return coreContent(organizationName, t);
}

function DataRetentionBanner({
  jwt,
  userRole,
  licensePlanType,
  currentDate = moment(),
  t,
} = {}) {
  const [showAnalyticsRetentionBanner, setShowAnalyticsRetentionBanner] = useState(false);
  const [showAuditLogRetentionBanner, setShowAuditLogRetentionBanner] = useState(false);
  const [analyticsBannerConfig, setAnalyticsBannerConfig] = useState({});
  const isOwner = userRole === 'OWNER';
  const organizationName = getJwtPayload(jwt).org;
  const {
    retention_period: retentionPeriod,
    start_date: startDate,
  } = useAnalyticsDataRetentionInfo({ isOwner, jwt });
  const {
    current_period: auditLogRetentionPeriod,
    oldest_event: auditLogOldestEvent,
  } = useAuditLogDataRetentionInfo({ isOwner, jwt });
  const analyticsDataRetentionCondition = () => {
    if (retentionPeriod === null || startDate === null) {
      setShowAnalyticsRetentionBanner(false);
    } else if (retentionPeriod && startDate && shouldShowAnalyticsBanner({
      retentionPeriod,
      startDate,
      currentDate,
    })) {
      setShowAnalyticsRetentionBanner(true);
      setAnalyticsBannerConfig(prepareBannerConfig({
        licensePlanType,
        organizationName,
        t,
      }));
    }
  };
  const auditLogDataRetentionCondition = () => {
    if (auditLogRetentionPeriod === null) {
      setShowAuditLogRetentionBanner(false);
    } else if (auditLogRetentionPeriod && shouldShowAuditLogBanner({
      retentionPeriod: auditLogRetentionPeriod,
      startDate: auditLogOldestEvent,
      currentDate,
    })) {
      setShowAuditLogRetentionBanner(true);
    }
  };

  useEffect(() => {
    if (!isOwner) {
      return;
    }
    analyticsDataRetentionCondition();
    auditLogDataRetentionCondition();
  }, [retentionPeriod, startDate, auditLogRetentionPeriod, auditLogOldestEvent, currentDate]);

  if (showAnalyticsRetentionBanner) {
    return (
      <StatusBar
        text={analyticsBannerConfig.text}
        type="warning"
        linkText={analyticsBannerConfig.linkText}
        onLinkClick={analyticsBannerConfig.onLinkClick}
        linkType="button"
        onClose={() => {
          setShowAnalyticsRetentionBanner(false);
          setClosedBannerTimestamp(LOCAL_STORAGE_NAMES.ANALYTICS, moment(currentDate).toISOString());
        }}
        closeIcon
      />
    );
  }

  if (showAuditLogRetentionBanner) {
    return (
      <StatusBar
        text={t('DataRetentionBanner.audit-log.core-and-paying.text')}
        type="warning"
        linkText={t('DataRetentionBanner.audit-log.core-and-paying.link-button.text')}
        onLinkClick={() => window.open('/administration/account/subscription', '_blank')}
        linkType="button"
        onClose={() => {
          setShowAuditLogRetentionBanner(false);
          setClosedBannerTimestamp(LOCAL_STORAGE_NAMES.AUDIT_LOG, moment(currentDate).toISOString());
        }}
        closeIcon
      />
    );
  }

  return null;
}

const enhanced = withTranslation('components')(DataRetentionBanner);

export { DataRetentionBanner as PureComponent };
export default enhanced;
